import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Flower2, Brain, Heart, Sparkles } from 'lucide-react';

export const Home = () => {
    return (
        <div className="min-h-screen">
            {/* Hero Section */}
            <section className="relative pt-32 pb-20 px-6 sm:px-8 lg:px-12 overflow-hidden">
                {/* Background Elements */}
                <div className="absolute inset-0 -z-10">
                    <div className="absolute top-20 left-10 w-72 h-72 bg-primary/10 rounded-full blur-3xl animate-float" />
                    <div className="absolute bottom-20 right-10 w-96 h-96 bg-secondary/10 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }} />
                </div>
                
                <div className="max-w-5xl mx-auto text-center">
                    {/* Decorative Icon */}
                    <div className="inline-flex items-center justify-center w-20 h-20 mb-8 rounded-full bg-primary/10 animate-breathe">
                        <Flower2 className="w-10 h-10 text-primary" />
                    </div>
                    
                    {/* Main Heading */}
                    <h1 className="text-5xl sm:text-6xl lg:text-7xl font-serif font-light text-foreground mb-6 leading-tight animate-fade-in">
                        Build Your Concentration
                        <br />
                        <span className="text-primary font-normal">One Breath at a Time</span>
                    </h1>
                    
                    {/* Subtitle */}
                    <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto mb-12 leading-relaxed animate-fade-in" style={{ animationDelay: '0.2s' }}>
                        Explore guided visual exercises to train your focus. Start with slow, mesmerizing animations designed to hold your attention and bring peace to your mind.
                    </p>
                    
                    {/* CTA Button */}
                    <Link to="/exercises" className="animate-fade-in" style={{ animationDelay: '0.4s' }}>
                        <Button 
                            size="lg" 
                            className="px-8 py-6 text-lg rounded-full bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
                        >
                            Begin Practice
                        </Button>
                    </Link>
                </div>
            </section>
            
            {/* Benefits Section */}
            <section className="py-20 px-6 sm:px-8 lg:px-12 bg-muted/20">
                <div className="max-w-6xl mx-auto">
                    <h2 className="text-3xl sm:text-4xl font-serif font-light text-center text-foreground mb-16">
                        Cultivate Inner Peace
                    </h2>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {/* Benefit 1 */}
                        <Card className="p-8 bg-card border-border hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                            <div className="inline-flex items-center justify-center w-16 h-16 mb-6 rounded-full bg-primary/10">
                                <Brain className="w-8 h-8 text-primary" />
                            </div>
                            <h3 className="text-xl font-serif font-medium text-foreground mb-4">
                                Enhanced Focus
                            </h3>
                            <p className="text-muted-foreground leading-relaxed">
                                Train your mind to stay present and attentive through gentle visual exercises that strengthen your concentration muscles.
                            </p>
                        </Card>
                        
                        {/* Benefit 2 */}
                        <Card className="p-8 bg-card border-border hover:shadow-lg transition-all duration-300 hover:-translate-y-1" style={{ transitionDelay: '0.1s' }}>
                            <div className="inline-flex items-center justify-center w-16 h-16 mb-6 rounded-full bg-secondary/10">
                                <Heart className="w-8 h-8 text-secondary" />
                            </div>
                            <h3 className="text-xl font-serif font-medium text-foreground mb-4">
                                Reduced Stress
                            </h3>
                            <p className="text-muted-foreground leading-relaxed">
                                Experience a calm mind as you engage with soothing animations that help melt away tension and anxiety.
                            </p>
                        </Card>
                        
                        {/* Benefit 3 */}
                        <Card className="p-8 bg-card border-border hover:shadow-lg transition-all duration-300 hover:-translate-y-1" style={{ transitionDelay: '0.2s' }}>
                            <div className="inline-flex items-center justify-center w-16 h-16 mb-6 rounded-full bg-accent/10">
                                <Sparkles className="w-8 h-8 text-accent" />
                            </div>
                            <h3 className="text-xl font-serif font-medium text-foreground mb-4">
                                Mindful Awareness
                            </h3>
                            <p className="text-muted-foreground leading-relaxed">
                                Develop a deeper connection with the present moment through simple, beautiful practices rooted in nature.
                            </p>
                        </Card>
                    </div>
                </div>
            </section>
            
            {/* How It Works Section */}
            <section className="py-20 px-6 sm:px-8 lg:px-12">
                <div className="max-w-4xl mx-auto text-center">
                    <h2 className="text-3xl sm:text-4xl font-serif font-light text-foreground mb-8">
                        Simple. Peaceful. Effective.
                    </h2>
                    <p className="text-lg text-muted-foreground mb-12 leading-relaxed max-w-2xl mx-auto">
                        Each exercise is designed to be effortlessly engaging. Just watch, breathe, and let your mind settle into a state of calm focus.
                    </p>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 text-left">
                        {/* Step 1 */}
                        <div className="flex flex-col items-center text-center">
                            <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center text-primary font-serif text-2xl mb-4">
                                1
                            </div>
                            <h3 className="text-lg font-medium text-foreground mb-2">Choose Exercise</h3>
                            <p className="text-sm text-muted-foreground">Select from our collection of calming visual practices</p>
                        </div>
                        
                        {/* Step 2 */}
                        <div className="flex flex-col items-center text-center">
                            <div className="w-12 h-12 rounded-full bg-secondary/20 flex items-center justify-center text-secondary font-serif text-2xl mb-4">
                                2
                            </div>
                            <h3 className="text-lg font-medium text-foreground mb-2">Set Your Time</h3>
                            <p className="text-sm text-muted-foreground">Adjust duration from 1 to 10 minutes based on your schedule</p>
                        </div>
                        
                        {/* Step 3 */}
                        <div className="flex flex-col items-center text-center">
                            <div className="w-12 h-12 rounded-full bg-accent/20 flex items-center justify-center text-accent font-serif text-2xl mb-4">
                                3
                            </div>
                            <h3 className="text-lg font-medium text-foreground mb-2">Focus & Breathe</h3>
                            <p className="text-sm text-muted-foreground">Let the animation guide your attention as you breathe deeply</p>
                        </div>
                    </div>
                </div>
            </section>
            
            {/* Final CTA */}
            <section className="py-20 px-6 sm:px-8 lg:px-12 bg-gradient-to-br from-primary/5 via-secondary/5 to-accent/5">
                <div className="max-w-3xl mx-auto text-center">
                    <h2 className="text-3xl sm:text-4xl font-serif font-light text-foreground mb-6">
                        Start Your Journey Today
                    </h2>
                    <p className="text-lg text-muted-foreground mb-10 leading-relaxed">
                        No sign-ups. No distractions. Just pure, mindful focus.
                    </p>
                    <Link to="/exercises">
                        <Button 
                            size="lg" 
                            className="px-8 py-6 text-lg rounded-full bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
                        >
                            Explore Exercises
                        </Button>
                    </Link>
                </div>
            </section>
        </div>
    );
};